import { hasEditorChanges } from "@ext/markdown/elements/image/render/logic/imageTransformer";
import { Fragment, type Node, type ResolvedPos } from "@tiptap/pm/model";
import { type Command, NodeSelection } from "@tiptap/pm/state";

/** Why a block image cannot move into a line of text: no text beside it, or parts an inline image has no room for. */
export type InlineBlocker = "no-text" | "block-parts";

interface Host {
	paragraph: Node;
	/** Right before the paragraph. */
	pos: number;
	above: boolean;
}

const hasText = (node: Node | null): node is Node => node?.type.name === "paragraph" && /\S/.test(node.textContent);

// The file keeps no mark of an inline image: one loads inline only beside text, so it may only join a paragraph that has some.
const findHost = (imagePos: ResolvedPos): Host | null => {
	const index = imagePos.index();
	const above = imagePos.parent.maybeChild(index - 1);
	const isTitle = imagePos.depth === 0 && index === 1;
	if (!isTitle && hasText(above)) return { paragraph: above, pos: imagePos.pos - above.nodeSize, above: true };

	const below = imagePos.parent.maybeChild(index + 1);
	if (hasText(below)) return { paragraph: below, pos: imagePos.pos + imagePos.nodeAfter.nodeSize, above: false };

	return null;
};

const hasBlockParts = (image: Node) =>
	Boolean(image.attrs.title) || hasEditorChanges(image.attrs.crop, image.attrs.objects);

const hasSpaceAt = (paragraph: Node, end: boolean) => {
	const edge = end ? paragraph.lastChild : paragraph.firstChild;
	return edge.isText && (end ? /\s$/ : /^\s/).test(edge.text);
};

export const getInlineBlocker = (doc: Node, pos: number): InlineBlocker | null => {
	const imagePos = doc.resolve(pos);
	if (!findHost(imagePos)) return "no-text";
	return hasBlockParts(imagePos.nodeAfter) ? "block-parts" : null;
};

export const convertImageToInline: Command = (state, dispatch) => {
	const { selection, schema } = state;
	if (!(selection instanceof NodeSelection) || selection.node.type.name !== "image") return false;
	const host = findHost(selection.$from);
	if (!host || hasBlockParts(selection.node)) return false;

	if (dispatch) {
		const { paragraph, pos, above } = host;
		const image = schema.nodes.inlineImage.create(selection.node.attrs);
		const gap = hasSpaceAt(paragraph, above) ? [] : [schema.text(" ")];
		const content = above
			? paragraph.content.append(Fragment.from([...gap, image]))
			: Fragment.from([image, ...gap]).append(paragraph.content);
		const from = above ? pos : selection.from;
		const to = above ? selection.to : pos + paragraph.nodeSize;
		// One step for the paragraph and the picture: a step that removes the image without adding it back deletes its file.
		const tr = state.tr.replaceWith(from, to, paragraph.copy(content));
		tr.setSelection(NodeSelection.create(tr.doc, from + 1 + (above ? paragraph.content.size + gap.length : 0)));
		dispatch(tr.scrollIntoView());
	}
	return true;
};
